import { useMutation } from '@apollo/client/react';
import { UTCDate } from '@date-fns/utc';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { ADJUST_MILESTONE_TIME } from '@optools/services/operator-gateway/milestones/mutations';
import { GET_MILESTONES } from '@optools/services/operator-gateway/milestones/queries';
import { Milestone } from '@optools/services/operator-gateway/milestones/types';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { formatInningDetails } from '../utils/milestones';
import { MilestoneForm, MilestoneFormValues } from './milestone-form';

export interface EditMilestoneDialogProps {
  mediaId: string;
  milestone: Milestone;
  originalTimestamp: string;
  programDateTime?: string;
}

export function EditMilestoneDialog({
  mediaId,
  milestone,
  originalTimestamp,
  programDateTime,
}: EditMilestoneDialogProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [adjustMilestone] = useMutation(ADJUST_MILESTONE_TIME, {
    onCompleted: ({ adjustMilestoneTime }) => {
      if (!adjustMilestoneTime) {
        toast.error('Adjust Milestone Failure', { duration: Infinity });
        return;
      }
      toast.success('Adjust Milestone Success');
      setIsOpen(false);
    },
    refetchQueries: [{ query: GET_MILESTONES, variables: { mediaId } }],
  });

  const { absoluteTime, id, milestoneType } = milestone;

  const onSubmit = (values: MilestoneFormValues) => {
    adjustMilestone({
      variables: {
        mediaId,
        milestoneId: id,
        timestamp: values.absoluteTime,
      },
    });
  };

  const onCancel = () => setIsOpen(false);

  const defaultValues = {
    absoluteTime: absoluteTime
      ? absoluteTime
      : programDateTime
        ? new UTCDate(programDateTime).toISOString()
        : new UTCDate().toISOString(),
    milestoneType,
    videoTimestamp: originalTimestamp,
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="outline">
          <Pencil />
          <span className="sr-only">Edit</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Milestone</DialogTitle>
          <DialogDescription className="sr-only">Adjust Milestone timestamp.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <dl className="flex items-center justify-start gap-2">
            <dt className="text-sm leading-none font-medium" id="edit-milestone-id">
              Id:
            </dt>
            <dd
              aria-labelledby="edit-milestone-id"
              className="text-sm leading-none font-medium opacity-70">
              {id}
            </dd>
          </dl>
          <dl className="flex items-center justify-start gap-2">
            <dt className="text-sm leading-none font-medium" id="edit-milestone-type">
              Type:
            </dt>
            <dd
              aria-labelledby="edit-milestone-type"
              className="text-sm leading-none font-medium opacity-70">
              {milestoneType}
            </dd>
          </dl>

          {milestone.keywords.length > 0 && (
            <div className="flex flex-wrap items-center justify-start gap-2">
              <dt className="text-sm leading-none font-medium" id="edit-milestone-details">
                Inning:
              </dt>
              <dd
                aria-labelledby="edit-milestone-details"
                className="text-sm leading-none font-medium opacity-70">
                {formatInningDetails(milestone)}
              </dd>
            </div>
          )}
          <MilestoneForm
            defaultValues={defaultValues}
            programDateTime={programDateTime}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
